import type { ZudokuConfig } from "zudoku";

const config: ZudokuConfig = {
  site: {
    logo: {
      src: { light: "/logo-light.svg", dark: "/logo-dark.svg" },
      alt: "Smart Code Junction Infinity Private Limited Logo",
      width: "130px",
    },
    title: "Smart Code Junction Infinity",
    showPoweredBy: false,
    footer: {
      position: "start",
      columns: [
        {
          title: "WhatsDiscuss",
          links: [
            { label: "Open Website", href: "https://whatsdiscuss.in" },
            { label: "Install WhatsDiscuss from Google Play", href: "https://whatsdiscuss.in/app" },
            { label: "Access WhatsDiscuss Web", href: "https://whatsdiscuss.in/web" },
            { label: "Help Center", href: "/whatsdiscuss-platform/overview" },
            { label: "Contact Us", href: "https://whatsdiscuss.in/contact" },
            { label: "Privacy Policy", href: "https://legal.smartcodejunctioninfinity.com/whatsdiscuss/privacy-policy" },
            { label: "Terms & Conditions", href: "https://legal.smartcodejunctioninfinity.com/whatsdiscuss/terms-and-conditions" },
            { label: "Refund Policy", href: "https://smartcodejunctioninfinity.com/legal/refund" }
          ]
        },
        {
          title: "SmartAcademy",
          links: [
            { label: "Open Website", href: "https://smartacademy.whatsdiscuss.in" },
            { label: "Install SmartAcademy from Google Play", href: "https://whatsdiscuss.in/appsa" },
            { label: "Access SmartAcademy Web", href: "https://smartacademy.whatsdiscuss.in/websa" },
            { label: "Help Center", href: "/smartacademy-platform/overview" },
            { label: "Contact Us", href: "https://smartacademy.whatsdiscuss.in/contact" },
            { label: "Privacy Policy", href: "https://legal.smartcodejunctioninfinity.com/smartacademy/privacy-policy" },
            { label: "Terms & Conditions", href: "https://legal.smartcodejunctioninfinity.com/smartacademy/terms-and-conditions" }
          ]
        },
        {
          title: "SmartCarePlus",
          links: [
            { label: "Open Website", href: "https://smartcareplus.in" },
            { label: "Install SmartCarePlus from Google Play", href: "https://smartcareplus.in/gplay" },
            { label: "Access SmartCarePlus Web", href: "https://web.smartcareplus.in/" },
            { label: "Help Center", href: "/smartcareplus-platform/overview" },
            { label: "Contact Us", href: "https://smartcareplus.in/contact" },
            { label: "Privacy Policy", href: "https://legal.smartcodejunctioninfinity.com/smartcareplus/privacy-policy" },
            { label: "Terms & Conditions", href: "https://legal.smartcodejunctioninfinity.com/smartcareplus/terms-and-conditions" }
          ]
        }
      ],
      copyright: `© ${new Date().getFullYear()} Smart Code Junction Infinity Private Limited | CIN: U62099UP2025PTC230480 | All rights reserved.`,
      logo: {
        src: {
          light: "/logo-light.svg",
          dark: "/logo-dark.svg"
        },
        alt: "Smart Code Junction Infinity Private Limited Logo",
        width: "100px"
      }
    },
  },
  metadata: {
    applicationName: "Smart Code Junction Infinity",
    title: "%s - Smart Code Junction Infinity Help Center",
    favicon: "/favicon.ico",
  },
  navigation: [
    {
      type: "category",
      label: "WhatsDiscuss",
      items: [
        { type: "doc", file: "whatsdiscuss-platform/introduction" },
        {
          type: "category",
          label: "Setup & Assistance",
          items: [
            { type: "doc", file: "whatsdiscuss-platform/about-free-trial" },
            {
              type: "link",
              label: "Contact Support",
              to: "https://whatsdiscuss.in/contact",
            },
          ]
        },
        {
          type: "category",
          label: "Features Overview",
          items: [
            { type: "doc", file: "whatsdiscuss-platform/features-overview/automation-bots" },
            { type: "doc", file: "whatsdiscuss-platform/features-overview/whatsapp-flows" },
            { type: "doc", file: "whatsdiscuss-platform/features-overview/whatsapp-payments" },
            { type: "doc", file: "whatsdiscuss-platform/features-overview/whatsapp-catalog" },
            { type: "doc", file: "whatsdiscuss-platform/features-overview/agents" },
            { type: "doc", file: "whatsdiscuss-platform/features-overview/carousel-templates" },
            { type: "doc", file: "whatsdiscuss-platform/features-overview/broadcasting" },
          ]
        },
        {
          type: "category",
          label: "How-To Guides",
          items: [
            { type: "doc", file: "whatsdiscuss-platform/how-to-guides/accessing-whatsdiscuss" },
            {
              type: "category",
              label: "Authentication",
              items: [
                { type: "doc", file: "whatsdiscuss-platform/how-to-guides/authentication/registering-for-a-whatsdiscuss-account" },
                { type: "doc", file: "whatsdiscuss-platform/how-to-guides/authentication/logging-into-whatsdiscuss" },
                { type: "doc", file: "whatsdiscuss-platform/how-to-guides/authentication/recovering-your-password" },
              ]
            },
            { type: "doc", file: "whatsdiscuss-platform/how-to-guides/dashboard-overview" },
            {
              type: "category",
              label: "WhatsApp Business Account",
              items: [
                { type: "doc", file: "whatsdiscuss-platform/how-to-guides/whatsapp-business-account/connecting-whatsapp-business-account" },
                { type: "doc", file: "whatsdiscuss-platform/how-to-guides/whatsapp-business-account/verifying-facebook-business-portfolio" },
              ]
            },
            {
              type: "category",
              label: "WhatsApp Profiles",
              items: [
                { type: "doc", file: "whatsdiscuss-platform/how-to-guides/whatsapp-profiles/about-whatsapp-profiles" },
                { type: "doc", file: "whatsdiscuss-platform/how-to-guides/whatsapp-profiles/about-messaging-limits" },
                { type: "doc", file: "whatsdiscuss-platform/how-to-guides/whatsapp-profiles/quality-ratings" },
                { type: "doc", file: "whatsdiscuss-platform/how-to-guides/whatsapp-profiles/create-whatsapp-profile" },
                { type: "doc", file: "whatsdiscuss-platform/how-to-guides/whatsapp-profiles/enable-disable-whatsapp-profile" },
                { type: "doc", file: "whatsdiscuss-platform/how-to-guides/whatsapp-profiles/verify-phone-number" },
                { type: "doc", file: "whatsdiscuss-platform/how-to-guides/whatsapp-profiles/enabling-whatsapp-profile-encryption" },
                { type: "doc", file: "whatsdiscuss-platform/how-to-guides/whatsapp-profiles/update-whatsapp-profile-details" },
                { type: "doc", file: "whatsdiscuss-platform/how-to-guides/whatsapp-profiles/update-whatsapp-profile-commerce-settings" },
                { type: "doc", file: "whatsdiscuss-platform/how-to-guides/whatsapp-profiles/whatsapp-profile-business-compliance-info" },
                { type: "doc", file: "whatsdiscuss-platform/how-to-guides/whatsapp-profiles/whatsapp-profile-insights" },
                { type: "doc", file: "whatsdiscuss-platform/how-to-guides/whatsapp-profiles/delete-whatsapp-profile" },
              ]
            },
            {
              type: "category",
              label: "Message Templates",
              items: [
                { type: "doc", file: "whatsdiscuss-platform/how-to-guides/message-templates/about-message-templates" },
                { type: "doc", file: "whatsdiscuss-platform/how-to-guides/message-templates/message-template-languages" },
                { type: "doc", file: "whatsdiscuss-platform/how-to-guides/message-templates/create-message-template" },
                { type: "doc", file: "whatsdiscuss-platform/how-to-guides/message-templates/edit-message-template-category" },
                { type: "doc", file: "whatsdiscuss-platform/how-to-guides/message-templates/edit-message-template-components" },
                { type: "doc", file: "whatsdiscuss-platform/how-to-guides/message-templates/configure-simple-message-template-components" },
                { type: "doc", file: "whatsdiscuss-platform/how-to-guides/message-templates/configure-carousel-media-message-template-components" },
                { type: "doc", file: "whatsdiscuss-platform/how-to-guides/message-templates/configure-lto-message-template-components" },
                { type: "doc", file: "whatsdiscuss-platform/how-to-guides/message-templates/configure-catalog-message-template-components" },
                { type: "doc", file: "whatsdiscuss-platform/how-to-guides/message-templates/configure-spm-message-template-components" },
                { type: "doc", file: "whatsdiscuss-platform/how-to-guides/message-templates/configure-mpm-message-template-components" },
                { type: "doc", file: "whatsdiscuss-platform/how-to-guides/message-templates/configure-carousel-product-message-template-components" },
                { type: "doc", file: "whatsdiscuss-platform/how-to-guides/message-templates/configure-order-details-message-template-components" },
                { type: "doc", file: "whatsdiscuss-platform/how-to-guides/message-templates/configure-order-status-message-template-components" },
                { type: "doc", file: "whatsdiscuss-platform/how-to-guides/message-templates/delete-message-template" },
              ]
            },
            {
              type: "category",
              label: "WhatsApp Flows",
              items: [
                { type: "doc", file: "whatsdiscuss-platform/how-to-guides/whatsapp-flows/about-whatsapp-flows" },
                { type: "doc", file: "whatsdiscuss-platform/how-to-guides/whatsapp-flows/whatsapp-flow-components" },
              ]
            },
            {
              type: "category",
              label: "WhatsApp Catalog",
              items: [
                { type: "doc", file: "whatsdiscuss-platform/how-to-guides/whatsapp-catalog/about-whatsapp-catalog" },
                { type: "doc", file: "whatsdiscuss-platform/how-to-guides/whatsapp-catalog/creating-a-catalogue" },
                { type: "doc", file: "whatsdiscuss-platform/how-to-guides/whatsapp-catalog/assigning-catalogue-permissions-to-code-junction" },
                { type: "doc", file: "whatsdiscuss-platform/how-to-guides/whatsapp-catalog/connecting-your-catalogue-with-waba" },
                { type: "doc", file: "whatsdiscuss-platform/how-to-guides/whatsapp-catalog/disconnecting-catalogue-from-waba" },
                { type: "doc", file: "whatsdiscuss-platform/how-to-guides/whatsapp-catalog/view-products" },
                { type: "doc", file: "whatsdiscuss-platform/how-to-guides/whatsapp-catalog/add-product" },
                { type: "doc", file: "whatsdiscuss-platform/how-to-guides/whatsapp-catalog/edit-product" },
                { type: "doc", file: "whatsdiscuss-platform/how-to-guides/whatsapp-catalog/delete-product" },
              ]
            },
            {
              type: "category",
              label: "Clients",
              items: [
                { type: "doc", file: "whatsdiscuss-platform/how-to-guides/clients/about-clients" },
                { type: "doc", file: "whatsdiscuss-platform/how-to-guides/clients/add-client" },
                { type: "doc", file: "whatsdiscuss-platform/how-to-guides/clients/import-clients-excel" },
                { type: "doc", file: "whatsdiscuss-platform/how-to-guides/clients/edit-client" },
                { type: "doc", file: "whatsdiscuss-platform/how-to-guides/clients/delete-client" },
              ]
            }

          ]
        },
      ],
    },
    {
      type: "category",
      label: "SmartAcademy",
      items: [
        {
          type: "category",
          label: "Overview",
          items: [
            { type: "doc", file: "smartacademy-platform/introduction" },
            { type: "doc", file: "smartacademy-platform/whats-new-may-2025" },
            { type: "doc", file: "smartacademy-platform/whats-new-april-2025" },
            { type: "doc", file: "smartacademy-platform/whats-new-february-2025" },
            { type: "doc", file: "smartacademy-platform/whats-new-january-2025" },
          ],
        },
        {
          type: "category",
          label: "How-To Guides",
          items: [
            { type: "doc", file: "smartacademy-platform/accessing-smartacademy" },
          ]
        },
      ]
    },
    {
      type: "category",
      label: "SmartAcademy V2",
      items: [
        { type: "doc", file: "smartacademy-v2-platform/index" },
        {
          type: "category",
          label: "Getting Started",
          items: [
            { type: "doc", file: "smartacademy-v2-platform/getting-started/overview" },
            { type: "doc", file: "smartacademy-v2-platform/getting-started/logging-in" },
            { type: "doc", file: "smartacademy-v2-platform/getting-started/register-school" },
            { type: "doc", file: "smartacademy-v2-platform/getting-started/reset-password" },
            { type: "doc", file: "smartacademy-v2-platform/getting-started/navigation-permissions" },
          ],
        },
        {
          type: "category",
          label: "Setup",
          items: [
            { type: "doc", file: "smartacademy-v2-platform/setup/organization-profile" },
            { type: "doc", file: "smartacademy-v2-platform/setup/academic-years" },
            { type: "doc", file: "smartacademy-v2-platform/setup/branches" },
            { type: "doc", file: "smartacademy-v2-platform/setup/branch-schedules" },
            { type: "doc", file: "smartacademy-v2-platform/setup/holidays" },
            { type: "doc", file: "smartacademy-v2-platform/setup/attendance-policies" },
          ],
        },
        {
          type: "category",
          label: "Academics",
          items: [
            { type: "doc", file: "smartacademy-v2-platform/academics/classes" },
            { type: "doc", file: "smartacademy-v2-platform/academics/sections" },
            { type: "doc", file: "smartacademy-v2-platform/academics/subjects" },
            { type: "doc", file: "smartacademy-v2-platform/academics/periods" },
            { type: "doc", file: "smartacademy-v2-platform/academics/timetables" },
          ],
        },
        {
          type: "category",
          label: "Admissions",
          items: [
            { type: "doc", file: "smartacademy-v2-platform/admissions/admission-inquiries" },
            { type: "doc", file: "smartacademy-v2-platform/admissions/convert-inquiry-to-student" },
          ],
        },
        {
          type: "category",
          label: "Students",
          items: [
            { type: "doc", file: "smartacademy-v2-platform/students/student-directory" },
            { type: "doc", file: "smartacademy-v2-platform/students/enroll-student" },
            { type: "doc", file: "smartacademy-v2-platform/students/student-profile-documents" },
            { type: "doc", file: "smartacademy-v2-platform/students/guardians" },
            { type: "doc", file: "smartacademy-v2-platform/students/promotions" },
            { type: "doc", file: "smartacademy-v2-platform/students/student-attendance" },
            { type: "doc", file: "smartacademy-v2-platform/students/student-leaves" },
          ],
        },
        {
          type: "category",
          label: "Staff",
          items: [
            { type: "doc", file: "smartacademy-v2-platform/staff/people-directory" },
            { type: "doc", file: "smartacademy-v2-platform/staff/roles-permissions" },
            { type: "doc", file: "smartacademy-v2-platform/staff/user-access-assignments" },
            { type: "doc", file: "smartacademy-v2-platform/staff/banking-details" },
            { type: "doc", file: "smartacademy-v2-platform/staff/change-user-password" },
            { type: "doc", file: "smartacademy-v2-platform/staff/staff-attendance" },
            { type: "doc", file: "smartacademy-v2-platform/staff/staff-leaves" },
            { type: "doc", file: "smartacademy-v2-platform/staff/overtime" },
            { type: "doc", file: "smartacademy-v2-platform/staff/salary-structures" },
            { type: "doc", file: "smartacademy-v2-platform/staff/payroll" },
          ],
        },
        {
          type: "category",
          label: "Finance",
          items: [
            { type: "doc", file: "smartacademy-v2-platform/finance/fee-categories" },
            { type: "doc", file: "smartacademy-v2-platform/finance/fee-structures" },
            { type: "doc", file: "smartacademy-v2-platform/finance/student-fee-assignments" },
            { type: "doc", file: "smartacademy-v2-platform/finance/collect-fees" },
            { type: "doc", file: "smartacademy-v2-platform/finance/transaction-history" },
            { type: "doc", file: "smartacademy-v2-platform/finance/fee-settings" },
            { type: "doc", file: "smartacademy-v2-platform/finance/public-online-payment" },
          ],
        },
        {
          type: "category",
          label: "Transport",
          items: [
            { type: "doc", file: "smartacademy-v2-platform/transport/vehicles" },
            { type: "doc", file: "smartacademy-v2-platform/transport/routes-stops" },
            { type: "doc", file: "smartacademy-v2-platform/transport/student-transport" },
          ],
        },
        {
          type: "category",
          label: "Daycare",
          items: [
            { type: "doc", file: "smartacademy-v2-platform/daycare/settings" },
            { type: "doc", file: "smartacademy-v2-platform/daycare/student-plans" },
            { type: "doc", file: "smartacademy-v2-platform/daycare/usage-billing" },
          ],
        },
        {
          type: "category",
          label: "Communication",
          items: [
            { type: "doc", file: "smartacademy-v2-platform/communication/whatsapp-profiles" },
          ],
        },
        {
          type: "category",
          label: "Integrations",
          items: [
            { type: "doc", file: "smartacademy-v2-platform/integrations/razorpay" },
            { type: "doc", file: "smartacademy-v2-platform/integrations/whatsdiscuss" },
          ],
        },
        {
          type: "category",
          label: "System",
          items: [
            { type: "doc", file: "smartacademy-v2-platform/system/dashboard" },
            { type: "doc", file: "smartacademy-v2-platform/system/audit-logs" },
            { type: "doc", file: "smartacademy-v2-platform/system/coming-soon" },
          ],
        },
      ]
    },
    {
      type: "category",
      label: "SmartCarePlus",
      items: [
        {
          type: "category",
          label: "Overview",
          items: [
            { type: "doc", file: "smartcareplus-platform/introduction" },
          ],
        },
        {
          type: "category",
          label: "How-To Guides",
          items: [
            {
              type: "category",
              label: "Getting Started",
              items: [
                { type: "doc", file: "smartcareplus-platform/accessing-smartcareplus" },
                { type: "doc", file: "smartcareplus-platform/logging-into-smartcareplus" },
                { type: "doc", file: "smartcareplus-platform/registering-on-smartcareplus" },
              ]
            },
            {
              type: "category",
              label: "Account Management",
              items: [
                { type: "doc", file: "smartcareplus-platform/resetting-your-account-password" },
                { type: "doc", file: "smartcareplus-platform/managing-your-smartcareplus-settings" },
                { type: "doc", file: "smartcareplus-platform/updating-your-smartcareplus-profile" },
                { type: "doc", file: "smartcareplus-platform/configuring-notification-preferences" },
              ]
            },
            {
              type: "category",
              label: "Pharmacy Management",
              items: [
                { type: "doc", file: "smartcareplus-platform/pharmacy-dashboard-overview" },
                { type: "doc", file: "smartcareplus-platform/manage-health-facilities" },
                { type: "doc", file: "smartcareplus-platform/adding-a-new-pharmacy" },
                { type: "doc", file: "smartcareplus-platform/editing-health-facility-details" },
                { type: "doc", file: "smartcareplus-platform/sharing-health-facility" },
                { type: "doc", file: "smartcareplus-platform/deleting-a-health-facility" },
                { type: "doc", file: "smartcareplus-platform/manage-staffs" },
                { type: "doc", file: "smartcareplus-platform/adding-a-new-staff" },
                { type: "doc", file: "smartcareplus-platform/editing-staff-details" },
                { type: "doc", file: "smartcareplus-platform/sharing-staff" },
                { type: "doc", file: "smartcareplus-platform/deleting-a-staff" },
                { type: "doc", file: "smartcareplus-platform/manage-medicines" },
                { type: "doc", file: "smartcareplus-platform/adding-a-new-medicine" },
                { type: "doc", file: "smartcareplus-platform/editing-medicine-details" },
                { type: "doc", file: "smartcareplus-platform/deleting-a-medicine" },
              ]
            },
          ]
        },
      ]
    }
  ],
  redirects: [
    { from: "/", to: "/introduction" },
    { from: "/whatsdiscuss-platform", to: "/whatsdiscuss-platform/overview" },
    { from: "/smartacademy-platform", to: "/smartacademy-platform/overview" },
    { from: "/smartacademy-v2-platform", to: "/smartacademy-v2-platform/index" },
    { from: "/smartcareplus-platform", to: "/smartcareplus-platform/overview" },
  ],
  search: {
    type: "pagefind",
    maxSubResults: 3,
    ranking: {
      termFrequency: 0.8,
      pageLength: 0.6,
      termSimilarity: 1.2,
      termSaturation: 1.2,
    },
  },
  sitemap: {
    siteUrl: 'https://help.smartcodejunctioninfinity.com',
    changefreq: 'weekly',
    priority: 0.5,
  },
  theme: {
    customCss: {
      "header nav": {
        margin: "auto"
      }
    },
  },
  docs: {
    files: "pages/**/*.mdx", // ← relative to DOCUMENT_ROOT (i.e., pages/)
    publishMarkdown: true, // Generate .md files
    llms: {
      llmsTxt: true, // Generate llms.txt
      llmsTxtFull: false, // Generate llms-full.txt
      includeProtected: false, // Exclude protected routes
    },
  },
};

export default config;
